import { CareNet, SystemName } from 'constants/captions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { setDocumentTitle } from 'shared/util'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'

import { OkIcon } from 'shared/icons'

import { CheckCircle as FinishedIcon } from '@mui/icons-material'

import { Loading } from 'components/controls/loading'

import { TenantResult, GetTenant, GetTenantBackground, GetTenantBanner } from 'interface/tenant'

import { useValidateForm, InviteValidateForm } from './form/validate'
import { useRegisterForm, UserRegisterForm } from './form/register'

import { useUserRegisterContainer } from './container'
import { useGetCurrentAgreementOpen } from 'interface/agreement'
import { RegisterFormData } from 'interface/user/register'
import { AgreementOpenPreviewDialog } from 'features/agreement/dialog/openPreview'

export const UserRegisterPage = () => {
  const [tenant, setTenant] = useState<TenantResult | undefined>()
  const [tenantBackground, setTenantBackground] = useState<string | undefined>()
  const [tenantBanner, setTenantBanner] = useState<string | undefined>()
  const [modal, setModal] = useState<'agree' | null>(null)
  const [registerParam, setRegisterParam] = useState<RegisterFormData | null>(null)

  const {
    domain,
    inviteId,
    isPosting,
    errorMessage,
    step,
    validateResult,
    registeredResult,
    onValidate,
    onRegister
  } = useUserRegisterContainer()

  const agreementState = useGetCurrentAgreementOpen(domain)

  const activeStep = useMemo(
    () => (
      step === 'validate' ? 0 :
      step === 'register' ? 1 :
      step === 'finished' ? 2 : 0
    ),
    [step]
  )
  const validateForm = useValidateForm(tenant, domain, inviteId)
  const registerForm = useRegisterForm(domain, validateResult, agreementState.data)

  useEffect(
    () => {
      if (!domain) return

      GetTenant(domain)
        .catch((reason) => console.error('failed to get tenant', reason))
        .then((result) => setTenant(result ?? undefined))

      GetTenantBackground(domain)
        .catch((reason) => console.error('failed to get tenant background', reason))
        .then((response) =>
          setTenantBackground(
            response ? URL.createObjectURL(response.blob) : undefined
          )
        )
      GetTenantBanner(domain)
        .catch((reason) => console.error('failed to get tenant bunner', reason))
        .then((response) =>
          setTenantBanner(
            response ? URL.createObjectURL(response.blob) : undefined
          )
        )
    },
    [domain]
  )
 
  const onAgree = useCallback(
    (version: number) => {
      setModal(null)
      if (registerParam) onRegister({ ...registerParam, agreementVersion: version })
    },
    [setModal, registerParam, onRegister]
  )

  useEffect(() => setDocumentTitle('ユーザー登録', tenant?.tenantName), [tenant])

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ backgroundColor: '#f0f0f0', minHeight: '100vh' }}
    >
      {(isPosting || agreementState.loading) && <Loading/>}

      <Container maxWidth="md">
        <Paper style={{ overflow: 'hidden' }}>
          <Box position="relative">
            {tenantBackground && (
              <img
                src={tenantBackground}
                alt={domain}
                style={{
                  objectFit: "cover",
                  width: "100%",
                  height: "25vh"
                }}
              />
            )}
            {tenantBanner && (
              <Box position="absolute" top="0.5em" left="0.5em">
                <img
                  src={tenantBanner}
                  alt={domain}
                  style={{
                    borderRadius: "0.2em"
                  }}
                />
              </Box>
            )}
          </Box>
          <Stack direction="column" spacing={5} padding={5}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ madginY: 5 }}>
              <Step key='validate'><StepLabel>認証</StepLabel></Step>
              <Step key='register'><StepLabel>登録</StepLabel></Step>
              <Step key='finished'><StepLabel>完了</StepLabel></Step>
            </Stepper>

            { step === 'validate' ? (
              <>
                <InviteValidateForm {...validateForm}/>

                {errorMessage && (
                  <Alert severity="error" sx={{ wordBreak: 'break-all' }}>
                    <AlertTitle>{errorMessage}</AlertTitle>
                  </Alert>
                )}
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    disabled={!validateForm.form.formState.isValid}
                    variant="contained"
                    onClick={validateForm.form.handleSubmit(input => onValidate(
                      validateForm.submit(input))
                    )}
                    startIcon={<OkIcon/>}
                    size="large"
                  >
                    認 証
                  </Button>
                </Stack>
              </>
            ) : step === 'register' ? (
              <>
                <UserRegisterForm {...registerForm}/>

                {errorMessage && (
                  <Alert severity="error" sx={{ wordBreak: 'break-all' }}>
                    <AlertTitle>{errorMessage}</AlertTitle>
                  </Alert>
                )}
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    disabled={!registerForm.form.formState.isValid || !validateResult || !validateResult.inviteToken}
                    variant="contained"
                    onClick={registerForm.form.handleSubmit(
                      input => {
                        if (!validateResult || !validateResult.inviteToken) return
                        const registerParam = registerForm.submit(input, validateResult.inviteToken)
                        if (agreementState.data) {
                          setRegisterParam(registerParam)
                          setModal('agree')
                        } else {
                          onRegister(registerParam)
                        }
                      }
                    )}
                    size="large"
                    startIcon={<OkIcon/>}
                  >
                    登 録
                  </Button>
                </Stack>
              </>
            ) : step === 'finished' ? (
              <>
                <Stack direction="column" alignItems="center">
                  <Box textAlign="center" paddingY={1} paddingX={4}>
                    <FinishedIcon color="success" fontSize="large"/>
                    <Typography variant="h5" marginTop={1}>
                      ユーザー登録が完了しました
                    </Typography>
                  </Box>
                </Stack>

                {registeredResult?.hasGroup ? (
                  <Stack direction="column" alignItems="center">
                    <Button
                      variant="contained"
                      href={`/${domain}/login`}
                      startIcon={<OkIcon/>}
                      size="large"
                    >
                      ログイン
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="column" alignItems="center" spacing={3}>
                    <Typography component="pre" variant='body1' textAlign="center">
                      管理者から承認された後、メールが届きます<br/>
                      メール内に記載されているURLからログインしてください
                    </Typography>

                    <Alert severity="warning">
                      <Typography component="pre" variant='body2'>
                        メールが届かない場合はお手数ですが<br/>
                        管理者までお問い合わせください
                      </Typography>
                    </Alert>
                  </Stack>
                )}
              </>
            ) : <></> }
          </Stack>
        </Paper>

        <Typography component="pre" variant='caption' textAlign="center" marginTop={2} color="GrayText">
          {CareNet} {SystemName} @ 2025
        </Typography>
      </Container>

      {(modal === 'agree' && agreementState.data) ? (
        <AgreementOpenPreviewDialog
          agreement={agreementState.data}
          onAgree={onAgree}
          onClose={() => setModal(null)}
        />
      ) : <></>}
    </Stack>
  )
}
